// Helper functions for call-related UI elements

export const CALL_DIRECTIONS = {
  INBOUND: 'Inbound',
  OUTBOUND: 'Outbound',
} as const;

export const GRADE_COLORS = {
  A: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  B: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  C: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  D: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  F: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
} as const;

export const DIRECTION_ICONS = {
  [CALL_DIRECTIONS.INBOUND]: '↓ In',
  [CALL_DIRECTIONS.OUTBOUND]: '↑ Out',
} as const;

export const NO_GRADE = 'N/A';
export const UNKNOWN_CUSTOMER = 'Unknown';
export const DEFAULT_CUSTOMER_LABEL = 'Customer';

// Get grade badge color classes
export function getGradeBadgeColor(grade: string): string {
  return (
    GRADE_COLORS[grade as keyof typeof GRADE_COLORS] ||
    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
  );
}

// Get direction icon
export function getDirectionIcon(direction: string): string {
  return DIRECTION_ICONS[direction as keyof typeof DIRECTION_ICONS] || '-';
}

// Get direction label
export function getDirectionLabel(direction: string): string {
  return DIRECTION_ICONS[direction as keyof typeof DIRECTION_ICONS] || direction;
}

// Check if grade should be displayed
export function shouldDisplayGrade(grade: string | null | undefined): boolean {
  return !!grade && grade !== NO_GRADE;
}

// Get customer display name
export function getCustomerDisplayName(customerName: string | null | undefined): string {
  return customerName && customerName !== UNKNOWN_CUSTOMER ? customerName : DEFAULT_CUSTOMER_LABEL;
}

// Format time for display (locale-aware)
export function formatCallTime(dateString: string, locale: string = 'en-US'): string {
  return new Date(dateString).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format date for display (locale-aware)
export function formatCallDate(dateString: string, locale: string = 'en-US'): string {
  return new Date(`${dateString}T00:00:00`).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format date header (locale-aware)
export function formatDateForHeader(dateString: string, locale: string = 'en-US'): string {
  if (!dateString) return '';
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format ISO datetime to local date and time (for call timestamps)
export function formatCallDateTime(isoString: string, locale: string = 'en-US'): string {
  if (!isoString) return 'Unknown time';
  const date = new Date(isoString);
  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Format ISO datetime to local date and time with year (for layout titles)
export function formatCallDateTimeFull(isoString: string, locale: string = 'en-US'): string {
  if (!isoString) return 'Unknown Time';
  const date = new Date(isoString);
  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Format ISO datetime to short date only (for compact displays)
export function formatCallDateShort(isoString: string, locale: string = 'en-US'): string {
  if (!isoString) return 'Unknown';
  const date = new Date(isoString);
  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
  });
}
